import { supabase } from '@/integrations/supabase/client'
import type { RouteTrims } from '@/lib/roads/trim'

// Share deep-links: /road/[publicId] and /route/[publicId]. Roads resolve
// through public RPCs (road data is public by design); routes resolve via a
// public RPC too — sharing is anonymous (one server row per distinct road
// list), and /route/[id] links open for anyone, signed in or not. Saved
// per-road trims ride along in saved_routes.trims (jsonb).

const roadIdByPublicCache = new Map<string, string | null>()
const publicIdByRoadCache = new Map<string, string | null>()

export async function roadIdByPublicId(publicId: string): Promise<string | null> {
  if (roadIdByPublicCache.has(publicId)) return roadIdByPublicCache.get(publicId)!
  const { data, error } = await supabase.rpc('road_id_by_public_id', { _public_id: publicId })
  if (error) return null
  const id = (data as string | null) ?? null
  roadIdByPublicCache.set(publicId, id)
  return id
}

export async function routeRoadIdsByPublicId(publicId: string): Promise<string[] | null> {
  const { data, error } = await supabase.rpc('route_road_ids_by_public_id', { _public_id: publicId })
  if (error) return null
  return (data as string[] | null) ?? null
}

// The name stamped on the route when it was saved/shared — shown in the tray
// header for whoever opens the shared link.
export async function routeNameByPublicId(publicId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('route_name_by_public_id', { _public_id: publicId })
  if (error) return null
  return (data as string | null) ?? null
}

// Trims saved with the shared route — keyed by road id, each with optional
// head/tail cuts. Guarded against non-object rows (schema drift, tampering).
export async function routeTrimsByPublicId(publicId: string): Promise<RouteTrims | null> {
  const { data, error } = await supabase.rpc('route_trims_by_public_id', { _public_id: publicId })
  if (error || typeof data !== 'object' || data === null || Array.isArray(data)) return null
  const out: RouteTrims = {}
  for (const [id, trim] of Object.entries(data as Record<string, unknown>)) {
    if (typeof trim !== 'object' || trim === null) continue
    const t = trim as { head?: unknown; tail?: unknown; reversed?: unknown }
    const clean: {
      head?: { pos: number; coord: [number, number] }
      tail?: { pos: number; coord: [number, number] }
      reversed?: boolean
    } = {}
    for (const end of ['head', 'tail'] as const) {
      const cut = t[end]
      if (
        typeof cut === 'object' &&
        cut !== null &&
        typeof (cut as { pos?: unknown }).pos === 'number' &&
        Array.isArray((cut as { coord?: unknown }).coord) &&
        (cut as { coord: unknown[] }).coord.length === 2 &&
        typeof (cut as { coord: unknown[] }).coord[0] === 'number' &&
        typeof (cut as { coord: unknown[] }).coord[1] === 'number'
      ) {
        clean[end] = {
          pos: (cut as { pos: number }).pos,
          coord: (cut as { coord: [number, number] }).coord,
        }
      }
    }
    if (typeof t.reversed === 'boolean') clean.reversed = t.reversed
    if (clean.head || clean.tail || clean.reversed) out[id] = clean
  }
  return Object.keys(out).length ? out : null
}

// The username of the account that saved/named the shared route — used to
// show "by X" in the tray header when opening a /route/[id] deep link.
export async function routeUsernameByPublicId(publicId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('route_username_by_public_id', {
    _public_id: publicId,
  })
  if (error) return null
  return (data as string | null) ?? null
}

export async function roadPublicId(roadId: string): Promise<string | null> {
  if (publicIdByRoadCache.has(roadId)) return publicIdByRoadCache.get(roadId)!
  const { data, error } = await supabase.rpc('road_public_id', { _road_id: roadId })
  if (error) return null
  const pid = (data as string | null) ?? null
  publicIdByRoadCache.set(roadId, pid)
  return pid
}

export async function roadShareUrl(roadId: string): Promise<string | null> {
  const pid = await roadPublicId(roadId)
  return pid ? `${window.location.origin}/road/${pid}` : null
}

// Resolve (or create, first share) the route's server row and return its
// permanent public URL. Works signed-in (caller's own row) and anonymous
// (shared row keyed to that exact road list) — no sign-in required. Every
// server-side route now carries the mandatory display name, and any active
// trims are persisted alongside it.
export async function routeShareUrl(
  roadIds: string[],
  name: string,
  routeId?: string | null,
  trims?: RouteTrims | null,
): Promise<string | null> {
  const trimsParam = trims && Object.keys(trims).length ? trims : null
  if (routeId) {
    const { data, error } = await supabase.rpc('share_route_public_id', {
      _road_ids: null,
      _name: name,
      _route_id: routeId,
      _trims: trimsParam,
    })
    if (!error && data) return `${window.location.origin}/route/${data}`
    // Stale or foreign UUID (route deleted elsewhere, or owned by another
    // account) — retry through the road-list path instead of failing.
  }
  if (roadIds.length === 0) return null
  const { data, error } = await supabase.rpc('share_route_public_id', {
    _road_ids: [...roadIds],
    _name: name,
    _route_id: null,
    _trims: trimsParam,
  })
  if (error || !data) return null
  return `${window.location.origin}/route/${data}`
}

// The signed-in user's own saved route identified by UUID: public id and
// current name. UUID is the primary server-side identity — road lists are
// only a fallback for legacy lookups.
export async function savedRouteByUuid(
  routeId: string,
): Promise<{ publicId: string | null; name: string | null; roadIds: string[] | null } | null> {
  const { data, error } = await supabase.rpc('my_saved_route_by_id', { _route_id: routeId })
  if (error) return null
  const row = (data as { id: string; public_id: string | null; road_ids: string[]; name: string | null }[] | null)?.[0]
  if (!row) return null
  return { publicId: row.public_id ?? null, name: row.name ?? null, roadIds: row.road_ids ?? [] }
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Sandboxed iframes deny the async Clipboard API — fall back to the
    // legacy execCommand path, which still works there.
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch {
      return false
    }
  }
}
